import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import AnimatedSpotAccount from '@utilitywarehouse/hearth-json-assets/lib/animated-spot-account-light.json';
import AnimatedSpotBoiler from '@utilitywarehouse/hearth-json-assets/lib/animated-spot-boiler-light.json';
import AnimatedSpotBroadbandSymbol from '@utilitywarehouse/hearth-json-assets/lib/animated-spot-broadband-symbol-light.json';
import AnimatedSpotInnovation from '@utilitywarehouse/hearth-json-assets/lib/animated-spot-innovation-light.json';
import AnimatedSpotProcessCompleteFunctional from '@utilitywarehouse/hearth-json-assets/lib/animated-spot-process-complete-functional-light.json';
import AnimatedSpotSavings2 from '@utilitywarehouse/hearth-json-assets/lib/animated-spot-savings-2-light.json';
import { Box, Button, Flex, Heading } from '@utilitywarehouse/hearth-react';

import { Dialog, DialogActions, DialogID, useDialog } from '@components/Dialog';
import { ProductSubtype, ProductType } from '@generated/gql/graphql';

enum UpdatingModalVariant {
  TEXT_ABOVE = 'above',
  TEXT_BELOW = 'below',
  ERROR = 'error',
}

interface UpdatingModalProps {
  heading: string;
  content?: string;
  variant?: UpdatingModalVariant;
  onClose?: () => void;
  id?: DialogID;
  productType?: ProductType;
  subType?: ProductSubtype;
  isRemoving?: boolean;
}

interface UpdatingModalUIProps {
  dialogId: DialogID;
  heading: string;
  content?: string;
  variant: UpdatingModalVariant;
  onClose?: () => void;
  productType?: ProductType;
  subType?: ProductSubtype;
  isRemoving?: boolean;
}

const getProductAnimation = (
  productType?: ProductType,
  subType?: ProductSubtype,
  isRemoving?: boolean
) => {
  if (isRemoving) return AnimatedSpotProcessCompleteFunctional;

  switch (productType) {
    case ProductType.Broadband:
      return AnimatedSpotBroadbandSymbol;
    case ProductType.Mobile:
      return AnimatedSpotAccount;
    case ProductType.CashbackCard:
      return AnimatedSpotSavings2;
    case ProductType.Insurance:
      if (subType === ProductSubtype.BoilerHomeCover) return AnimatedSpotBoiler;
      return AnimatedSpotInnovation;
    default:
      return AnimatedSpotInnovation;
  }
};

const LoadingAnimation = ({
  productType,
  subType,
  isRemoving,
}: {
  productType?: ProductType;
  subType?: ProductSubtype;
  isRemoving?: boolean;
}) => (
  <Box width="120px" height="135px">
    <DotLottieReact
      data={getProductAnimation(productType, subType, isRemoving)}
      loop
      autoplay
    />
  </Box>
);

const UpdatingModalUI = ({
  dialogId,
  heading,
  content,
  variant,
  onClose,
  productType,
  subType,
  isRemoving,
}: UpdatingModalUIProps) => {
  const isError = variant === UpdatingModalVariant.ERROR;
  const isTextAbove = !isError && variant === UpdatingModalVariant.TEXT_ABOVE;
  const isTextBelow = !isError && variant === UpdatingModalVariant.TEXT_BELOW;

  return (
    <Dialog
      id={dialogId}
      description={isError ? content : undefined}
      heading={isError ? heading : undefined}
      hideCloseButton
      preventDismiss
    >
      {!isError && (
        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          spacing="xl"
          padding="400"
        >
          {isTextBelow && (
            <LoadingAnimation
              productType={productType}
              subType={subType}
              isRemoving={isRemoving}
            />
          )}
          <Heading as="h2" size="lg" textAlign="center">
            {heading}
          </Heading>
          {isTextAbove && (
            <LoadingAnimation
              productType={productType}
              subType={subType}
              isRemoving={isRemoving}
            />
          )}
        </Flex>
      )}

      {isError && (
        <DialogActions>
          <Button variant="solid" colorScheme="destructive" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

const UpdatingModal = ({
  heading,
  content,
  variant = UpdatingModalVariant.TEXT_BELOW,
  onClose,
  id = DialogID.UPDATING_BUNDLE,
  productType,
  subType,
  isRemoving,
}: UpdatingModalProps) => {
  const dialog = useDialog(id);

  const handleClose = () => {
    dialog.setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <UpdatingModalUI
      dialogId={dialog.id}
      heading={heading}
      content={content}
      variant={variant}
      onClose={handleClose}
      productType={productType}
      subType={subType}
      isRemoving={isRemoving}
    />
  );
};

export type { UpdatingModalProps, UpdatingModalUIProps };
export { UpdatingModal, UpdatingModalUI, UpdatingModalVariant };
